import { useCallback, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { currentLocation, subscribeHistoryUi } from '../lib/historyUiStore'
import { useTabActive } from './useTabVisit'

interface UiHistoryState {
  ui?: Record<string, unknown>
}

const MISSING = Symbol('missing')

/**
 * `useState` that lives in the current history entry's state, so a dialog left
 * open when navigating away (e.g. tapping a member to open their profile) is
 * still open after swiping back. Not in the URL: page_view tracking stays put
 * and a fresh visit starts clean. `key` must be unique on the page; pass null
 * to fall back to plain local state.
 *
 * Store related values (open + tab) as one value — two setter calls in the same
 * event would each start from the same stale entry state.
 *
 * Subscribes to its own key only, so a navigation re-renders just the
 * components whose value changed. A hidden kept-alive tab reads `initial`: the
 * current entry belongs to another route, and its floaters render nothing anyway.
 */
export function useHistoryState<T>(key: string | null, initial: T): [T, (value: T) => void] {
  const navigate = useNavigate()
  const tabActive = useTabActive()
  const [local, setLocal] = useState(initial)
  const stored = useSyncExternalStore(subscribeHistoryUi, () => {
    if (!key || !tabActive) return MISSING
    const ui = (currentLocation()?.state as UiHistoryState | null)?.ui
    return ui && key in ui ? ui[key] : MISSING
  })
  const value = key ? (stored === MISSING ? initial : (stored as T)) : local

  const setValue = useCallback((next: T) => {
    if (!key) return setLocal(next)
    const location = currentLocation()
    if (!location) return
    const prev = location.state as (UiHistoryState & object) | null
    const state: UiHistoryState = { ...prev, ui: { ...prev?.ui, [key]: next } }
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state })
  }, [key, navigate])

  return [value, setValue]
}
